import * as THREE from "three";
import { VOXEL_SIZE } from "./VoxelInstanceBuffer";
import type { VoxelData } from "./voxelTypes";

/**
 * Builds a standalone group of instanced voxels, batched by colour.
 *
 * The static world goes through `VoxelInstanceManager`, which fuses everything into a handful of
 * enormous meshes that cannot afterwards be moved or picked out individually. Anything that has to
 * turn, slide or be clicked needs its own group instead — the windmill sails, the watermill wheel,
 * and every item a member puts in their yard.
 *
 * Per-voxel scale carries the shape here. The thin sail spars, the canvas panels and the wheel's
 * bucket paddles are all sub-voxel boxes, and rendering them as full cubes fuses each assembly
 * into a solid blob.
 */
export function buildVoxelMeshGroup(voxels: VoxelData[], options?: { opacity?: number }): THREE.Group {
  const group = new THREE.Group();
  const geometry = new THREE.BoxGeometry(VOXEL_SIZE * 1.002, VOXEL_SIZE * 1.002, VOXEL_SIZE * 1.002);
  const byColour = new Map<number, VoxelData[]>();

  for (const voxel of voxels) {
    const colour = typeof voxel.color === "string" ? parseInt(voxel.color.replace("#", ""), 16) : voxel.color;
    const batch = byColour.get(colour);
    if (batch) batch.push(voxel);
    else byColour.set(colour, [voxel]);
  }

  const ghosted = options?.opacity !== undefined && options.opacity < 1;
  const dummy = new THREE.Object3D();

  for (const [colour, batch] of byColour) {
    const material = new THREE.MeshStandardMaterial({
      color: colour,
      roughness: 0.8,
      metalness: 0.1,
      ...(ghosted ? { transparent: true, opacity: options!.opacity, depthWrite: false } : {})
    });
    const mesh = new THREE.InstancedMesh(geometry, material, batch.length);
    batch.forEach((voxel, index) => {
      dummy.position.set(voxel.x * VOXEL_SIZE, voxel.y * VOXEL_SIZE, voxel.z * VOXEL_SIZE);
      dummy.scale.set(
        voxel.scaleX ?? voxel.scale ?? 1,
        voxel.scaleY ?? voxel.scale ?? 1,
        voxel.scaleZ ?? voxel.scale ?? 1
      );
      dummy.updateMatrix();
      mesh.setMatrixAt(index, dummy.matrix);
    });
    mesh.instanceMatrix.needsUpdate = true;
    mesh.castShadow = !ghosted;
    mesh.receiveShadow = !ghosted;
    group.add(mesh);
  }

  return group;
}

/** Releases the geometry and materials a group built here is holding. */
export function disposeVoxelMeshGroup(group: THREE.Object3D): void {
  group.traverse((node) => {
    if (!(node instanceof THREE.Mesh)) return;
    node.geometry.dispose();
    const material = node.material;
    if (Array.isArray(material)) for (const entry of material) entry.dispose();
    else material.dispose();
  });
}
